import { apiUtils } from '../hooks';
import { throwApiError } from '../utils';
import { API_ENDPOINTS } from './apiEndpoints';

export interface MaintenanceImpactSlide {
  id: string;
  title: string;
  metric_value: string;
  metric_type: 'absolute' | 'percentage' | string;
  description: string;
  display_order: number;
  is_active: boolean;
}

export interface ImpactSlidePayload {
  title?: string;
  metric_value: string;
  metric_type?: 'absolute' | 'percentage' | string;
  description: string;
  display_order?: number;
  is_active?: boolean;
}

export type LifeCycleSummaryData = Record<string, any>;

export interface LifeCycleSummaryResponse {
  success: boolean;
  data: LifeCycleSummaryData;
}

export interface FileUploadResult {
  parsing: {
    brands_processed: number;
    total_chain_codes: number;
    duplicates_found: number;
    skipped_rows: number;
  };
  counts: {
    inserted_count: number;
    duplicate_count: number;
    invalid_count: number;
    not_inserted_count: number;
  };
  not_inserted: Array<{
    type: string;
    brand: string;
    brandCode: string;
    reason: string;
  }>;
}

interface OverrideFileUploadResponse {
  success: boolean;
  message: string;
  data: FileUploadResult;
}
class MaintenanceService {
  /**
   *
   * @param file
   * @returns Promise<OverrideFileUploadResponse>
   * @throws Error if the upload fails
   * @description Uploads a hotel brand override file to the server.
   */
  async uploadHotelBrandOverrideFile(
    formData: FormData
  ): Promise<OverrideFileUploadResponse> {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.maintenance.uploadHotelBrandOverrideFile,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      console.log('Response:', response);
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to upload hotel brand override file');
      throw error;
    }
  }
  /**
   * 
   * @returns Promise<any>
   * @throws Error if the sync operation fails
   * @description Triggers the synchronization of brand data to the hotels table.
   */
  async syncBrandDataToHotelsTable() {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.maintenance.syncData
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to sync data');
      throw error;
    }
  }

  async getImpactSlides(): Promise<{ success: boolean; data: MaintenanceImpactSlide[] }> {
    try {
      const response = await apiUtils.get<{ success: boolean; data: MaintenanceImpactSlide[] }>(
        API_ENDPOINTS.maintenance.impactSlides
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch impact slides');
      throw error;
    }
  }

  async createImpactSlide(
    payload: ImpactSlidePayload
  ): Promise<{ success: boolean; data: MaintenanceImpactSlide }> {
    try {
      const response = await apiUtils.post<{ success: boolean; data: MaintenanceImpactSlide }>(
        API_ENDPOINTS.maintenance.impactSlides,
        payload
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to create impact slide');
      throw error;
    }
  }

  async updateImpactSlide(
    slideId: string,
    payload: Partial<ImpactSlidePayload>
  ): Promise<{ success: boolean; data: MaintenanceImpactSlide }> {
    try {
      const response = await apiUtils.put<{ success: boolean; data: MaintenanceImpactSlide }>(
        API_ENDPOINTS.maintenance.updateImpactSlide(slideId),
        payload
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to update impact slide');
      throw error;
    }
  }

  async deleteImpactSlide(slideId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiUtils.delete<{ success: boolean; message: string }>(
        API_ENDPOINTS.maintenance.deleteImpactSlide(slideId)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to delete impact slide');
      throw error;
    }
  }

  async getLifeCycleSummary(hotelId: string): Promise<LifeCycleSummaryResponse> {
    try {
      const response = await apiUtils.get<LifeCycleSummaryResponse>(
        API_ENDPOINTS.maintenance.lifeCycleEventsSummary(hotelId)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch lifecycle summary');
      throw error;
    }
  }
}

export const maintenanceService = new MaintenanceService();
